import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import chatService, { ChatEntryType } from '../services/chatService';

const TEAL = '#3FCED2';

interface StartChatButtonProps {
  entryId?: string | null;
  entryType: ChatEntryType;
  label?: string;
}

/**
 * Wiederverwendbarer Button "Nachricht senden".
 * - Nicht eingeloggt → Weiterleitung zu /login
 * - Eingeloggt → POST /api/chat/start, danach Weiterleitung zu /nachrichten/:chatId
 */
const StartChatButton: React.FC<StartChatButtonProps> = ({ entryId, entryType, label }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!entryId) return null;

  const handleClick = async () => {
    if (loading) return;
    setError(null);

    if (!isAuthenticated) {
      navigate('/login', { state: { from: location } });
      return;
    }

    setLoading(true);
    try {
      const chat = await chatService.startChat(entryId, entryType);
      if (chat?.id) {
        navigate(`/nachrichten/${chat.id}`);
      } else {
        setError('Chat konnte nicht gestartet werden.');
      }
    } catch (err: any) {
      setError(chatService.extractErrorMessage(err, 'Chat konnte nicht gestartet werden.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border font-semibold text-sm transition hover:bg-zinc-800 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ borderColor: TEAL, color: TEAL, backgroundColor: 'transparent' }}
      >
        {loading ? (
          <span
            className="w-4 h-4 border-2 rounded-full animate-spin"
            style={{ borderColor: 'rgba(63,206,210,0.3)', borderTopColor: TEAL }}
          />
        ) : (
          <MessageCircle size={18} />
        )}
        {label || 'Nachricht senden'}
      </button>
      {error && (
        <p className="mt-2 text-xs text-red-400">{error}</p>
      )}
    </div>
  );
};

export default StartChatButton;
